import { Link, LinkProps, Stack, StackProps, styled, Typography } from '@mui/material'

import { ParkingLot } from 'models/ParkingLot.model'
import { FC } from 'react'

const AutocompleteStack = styled(Stack)(({ theme }) => ({
  background: theme.palette.grey[800],
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[4],
  position: 'absolute',
  right: theme.spacing(4),
  zIndex: 10000,
  minWidth: 'max-content',
  padding: theme.spacing(1),
  left: 0,
  top: '100%',
  [theme.breakpoints.down('sm')]: {
    left: theme.spacing(1),
    right: theme.spacing(1),
  },
}))

const AutocompleteLink = styled(Stack)<StackProps & LinkProps>(({ theme }) => ({
  '&:hover': {
    background: theme.palette.grey[700],
  },
  color: '#fff',
  padding: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.25),
  paddingTop: theme.spacing(1.25),
}))

type ParkingLotDropdownProps = {
  parkingLots?: Partial<ParkingLot>[]
  isOpen: boolean
}

export const ParkingLotDropdown: FC<ParkingLotDropdownProps> = ({ parkingLots, isOpen }) => {
  // Always return a valid React element, even when not showing
  if (!isOpen) {
    return <></>
  }
  // If there are no parking lots, show a message
  if (!parkingLots?.length) {
    return (
      <AutocompleteStack>
        <AutocompleteLink direction="row" alignItems="center" spacing={1.5} component={Link} href="#">
          <Typography fontSize={15}>No parking lots available</Typography>
        </AutocompleteLink>
      </AutocompleteStack>
    )
  }

  return (
    <AutocompleteStack>
      {parkingLots?.map((parkingLot) => {
        const { gpsLat, gpsLong, id, name } = parkingLot
        // if gpsLat and gpsLong are valid numbers, create a link to Google Maps
        if (typeof gpsLat === 'number' && typeof gpsLong === 'number') {
          return (
            <AutocompleteLink
              direction="row"
              alignItems="center"
              spacing={1.5}
              component={Link}
              key={id}
              href={`https://www.google.com/maps/search/?api=1&query=${gpsLat},${gpsLong}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Typography fontSize={15}>{name}</Typography>
            </AutocompleteLink>
          )
        } else {
          // show a message if gpsLat or gpsLong are not valid
          return (
            <AutocompleteLink direction="row" alignItems="center" spacing={1.5} key={id} component={Stack}>
              <Typography fontSize={15} color="error">
                Location unavailable
              </Typography>
            </AutocompleteLink>
          )
        }
      })}
    </AutocompleteStack>
  )
}
ParkingLotDropdown.displayName = 'ParkingLotDropdown'
